import type { Text } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, TYPE } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, Panel, makeText, sceneBackground } from '../ui/widgets';
import { Spinner } from '../ui/Spinner';
import type { AppContext } from '../app/AppContext';
import { announceStatus } from '../ui/accessibility';
import { hapticNotification } from '../platform/telegram';

/**
 * Full-screen "connection lost" page shown by the ReconnectController when the socket drops while the
 * player is NOT in a game (lobby/menu). Mirrors the Unity Connect page: a status line the controller
 * drives ("تلاش تا n ثانیه دیگر…"), a "تلاش مجدد" button to force an immediate attempt, and a
 * "بازگشت به ورود" escape. The controller auto-retries every few seconds on its own.
 */
export class ReconnectScene extends Scene {
  private statusText!: Text;
  private lastStatus = 'در حال اتصال مجدد…';

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.build();
    hapticNotification('error');
    announceStatus('اتصال قطع شد. برنامه در حال تلاش برای اتصال مجدد است.', true);
  }

  /** Re-flow on viewport-height change: re-center the card for the live height. */
  onResize(): void {
    this.build();
  }

  private build(): void {
    for (const ch of this.removeChildren()) ch.destroy({ children: true });
    this.addChild(sceneBackground());

    const cardW = 560;
    const cardH = 520;
    const cardX = (DESIGN_W - cardW) / 2;
    const cardY = (viewport.h - cardH) / 2;

    const panel = new Panel(cardW, cardH, { fill: COLORS.panel, alpha: 0.9, shadow: true });
    panel.position.set(cardX, cardY);
    this.addChild(panel);

    const title = makeText('اتصال اینترنت قطع شد', { size: 38, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, cardY + 42);
    this.addChild(title);

    const spinner = new Spinner();
    spinner.position.set(DESIGN_W / 2, cardY + 168);
    this.addChild(spinner);

    this.statusText = makeText(this.lastStatus, { size: TYPE.body, align: 'center', fill: COLORS.text });
    this.statusText.anchor.set(0.5, 0);
    this.statusText.style.wordWrap = true;
    this.statusText.style.wordWrapWidth = cardW - 80;
    this.statusText.position.set(DESIGN_W / 2, cardY + 244);
    this.addChild(this.statusText);

    const btnW = cardW - 120;
    const btnX = (DESIGN_W - btnW) / 2;
    const retry = new Button('تلاش مجدد', () => this.ctx.reconnect?.retryNow(), {
      width: btnW,
      height: 88,
      fontSize: 28,
      icon: this.ctx.iconTexture('refresh-page-option') ?? null,
    });
    retry.position.set(btnX, cardY + cardH - 196);
    this.addChild(retry);

    const back = new Button('بازگشت به ورود', () => this.ctx.reconnect?.cancelToLogin(), {
      width: btnW,
      height: 76,
      fontSize: 26,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    back.position.set(btnX, cardY + cardH - 92);
    this.addChild(back);
  }

  /** Called by the ReconnectController to reflect the current attempt/countdown state. */
  setStatus(text: string): void {
    this.lastStatus = text;
    if (this.statusText && !this.statusText.destroyed) this.statusText.text = text;
    announceStatus(text);
  }
}
